import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import cn from 'classnames'

import { Button } from 'components'
import { copyTextClipboard, setUrlSearchParams, showAlertNotify } from 'utils/helpers'
import { useAppSelector } from 'store'
import style from './Share.module.css'

enum PlatformName {
    facebook = 'facebook',
    vk = 'vk',
    twitter = 'twitter',
    telegram = 'telegram',
    whatsapp = 'whatsapp',
}

type TPlatform = {
    url: string
    paramText: string
    paramUrl: string
    iconViewBox: number
    iconColor: string
    iconFillColor: string
    iconPath: string
}

type TShareItem = { key: string, shareLink: string } & TPlatform

type SharePropType = {
    link: string
    text: string
    classes?: string
    platforms?: (keyof typeof PlatformName)[]
    iconParams?: {
        size?: number
        borderRadius?: number
    }
}

const PLATFORMS: Record<keyof typeof PlatformName, TPlatform> = {
    [PlatformName.facebook]: {
        url: 'https://www.facebook.com/sharer.php',
        paramText: '',
        paramUrl: 'u',
        iconViewBox: 64,
        iconColor: '#3b5998',
        iconFillColor: '#fff',
        iconPath: 'M34.1,47V33.3h4.6l0.7-5.3h-5.3v-3.4c0-1.5,0.4-2.6,2.6-2.6l2.8,0v-4.8c-0.5-0.1-2.2-0.2-4.1-0.2 c-4.1,0-6.9,2.5-6.9,7V28H24v5.3h4.6V47H34.1z',
    },
    [PlatformName.vk]: {
        url: 'https://vk.com/share.php',
        paramText: '',
        paramUrl: 'url',
        iconViewBox: 64,
        iconColor: '#45668e',
        iconFillColor: '#fff',
        iconPath: 'M44.94,44.84h-0.2c-2.17-.36-3.66-1.92-4.92-3.37C39.1,40.66,38,38.81,36.7,39c-1.85.3-.93,3.52-1.71,4.9-0.62,1.11-3.29.91-5.12,0.71-5.79-.62-8.75-3.77-11.35-7.14A64.13,64.13,0,0,1,11.6,26a10.59,10.59,0,0,1-1.51-4.49C11,20.7,12.56,21,14.11,21c1.31,0,3.36-.29,4.32.2C19,21.46,19.57,23,20,24a37.18,37.18,0,0,0,3.31,5.82c0.56,0.81,1.41,2.35,2.41,2.14s1.06-2.63,1.1-4.18c0-1.77,0-4-.5-4.9S25,22,24.15,21.47c0.73-1.49,2.72-1.63,5.12-1.63,2,0,4.84-.23,5.62,1.12s0.25,3.85.2,5.71c-0.06,2.09-.41,4.25,1,5.21,1.09-.12,1.68-1.2,2.31-2A28,28,0,0,0,41.72,24c0.44-1,.91-2.65,1.71-3,1.21-.47,3.15-0.1,4.92-0.1,1.46,0,4.05-.41,4.52.61,0.39,0.85-.75,3-1.1,3.57a61.88,61.88,0,0,1-4.12,5.61c-0.58.78-1.78,2-1.71,3.27,0.05,0.94,1,1.67,1.71,2.35a33.12,33.12,0,0,1,3.92,4.18c0.47,0.62,1.5,2,1.4,2.76C52.66,45.81,46.88,44.24,44.94,44.84Z',
    },
    [PlatformName.twitter]: {
        /** @see https://developer.x.com/en/docs/twitter-for-websites/tweet-button/guides/web-intent */
        url: 'https://twitter.com/intent/tweet',
        paramText: 'text',
        paramUrl: 'url',
        iconViewBox: 512,
        iconColor: '#000',
        iconFillColor: '#fafafa',
        iconPath: 'M385.4,52.6h69L303.7,224.9L481,459.4H342.1L233.4,317.2L108.9,459.4h-69l161.2-184.3L31,52.6h142.4l98.3,130L385.4,52.6zM361.2,418.1h38.2L152.6,91.8h-41L361.2,418.1z',
    },
    [PlatformName.telegram]: {
        url: 'https://t.me/share/url',
        paramText: 'text',
        paramUrl: 'url',
        iconViewBox: 64,
        iconColor: '#37aee2',
        iconFillColor: '#fff',
        iconPath: 'm45.90873,15.44335c-0.6901,-0.0281 -1.37668,0.14048 -1.96142,0.41265c-0.84989,0.32661 -8.63939,3.33986 -16.5237,6.39174c-3.9685,1.53296 -7.93349,3.06593 -10.98537,4.24067c-3.05012,1.1765 -5.34694,2.05098 -5.4681,2.09312c-0.80775,0.28096 -1.89996,0.63566 -2.82712,1.72788c-0.23354,0.27218 -0.46884,0.62161 -0.58825,1.10275c-0.11941,0.48114 -0.06673,1.09222 0.16682,1.5716c0.46533,0.96052 1.25376,1.35737 2.18443,1.71383c3.09051,0.99037 6.28638,1.93508 8.93263,2.8236c0.97632,3.44171 1.91401,6.89571 2.84116,10.34268c0.30554,0.69185 0.97105,0.94823 1.65764,0.95525l-0.00351,0.03512c0,0 0.53908,0.05268 1.06412,-0.07375c0.52679,-0.12292 1.18879,-0.42846 1.79109,-0.99212c0.662,-0.62161 2.45836,-2.38812 3.47683,-3.38552l7.6736,5.66477l0.06146,0.03512c0,0 0.84989,0.59703 2.09312,0.68132c0.62161,0.04214 1.4399,-0.07726 2.14229,-0.59176c0.70766,-0.51626 1.1765,-1.34683 1.396,-2.29506c0.65673,-2.86224 5.00979,-23.57745 5.75257,-27.00686l-0.02107,0.08077c0.51977,-1.93157 0.32837,-3.70159 -0.87096,-4.74991c-0.60054,-0.52152 -1.2924,-0.7498 -1.98425,-0.77965l0,0.00176zm-0.2072,3.29069c0.04741,0.0439 0.0439,0.0439 0.00351,0.04741c-0.01229,-0.00351 0.14048,0.2072 -0.15804,1.32576l-0.01229,0.04214l-0.00878,0.03863c-0.75858,3.50668 -5.15554,24.40802 -5.74203,26.96472c-0.08077,0.34417 -0.11414,0.31959 -0.09482,0.29852c-0.1756,-0.02634 -0.50045,-0.16506 -0.52679,-0.1756l-13.13468,-9.70175c4.4988,-4.33199 9.09945,-8.25307 13.744,-12.43229c0.8218,-0.41265 0.68483,-1.68573 -0.29852,-1.70681c-1.04305,0.24584 -1.92279,0.99564 -2.8798,1.47502c-5.49971,3.2626 -11.11882,6.13186 -16.55882,9.49279c-2.792,-0.97105 -5.57873,-1.77704 -8.15298,-2.57601c2.2336,-0.89555 4.00889,-1.55579 5.75608,-2.23009c3.05188,-1.1765 7.01687,-2.7042 10.98537,-4.24067c7.94051,-3.06944 15.92667,-6.16346 16.62028,-6.43037l0.05619,-0.02283l0.05268,-0.02283c0.19316,-0.0878 0.30378,-0.09658 0.35471,-0.10009c0,0 -0.01756,-0.05795 -0.00351,-0.04566l-0.00176,0zm-20.91715,22.0638l2.16687,1.60145c-0.93418,0.91311 -1.81743,1.77353 -2.45485,2.38812l0.28798,-3.98957',
    },
    [PlatformName.whatsapp]: {
        url: 'https://wa.me',
        paramText: 'text',
        paramUrl: '',
        iconViewBox: 64,
        iconColor: '#25D366',
        iconFillColor: '#fff',
        iconPath: 'm42.32286,33.93287c-0.5178,-0.2589 -3.04726,-1.49644 -3.52105,-1.66732c-0.4712,-0.17346 -0.81554,-0.2589 -1.15987,0.2589c-0.34175,0.51004 -1.33075,1.66474 -1.63108,2.00648c-0.30032,0.33658 -0.60064,0.36247 -1.11327,0.12945c-0.5178,-0.2589 -2.17994,-0.80259 -4.14759,-2.56312c-1.53269,-1.37217 -2.56312,-3.05503 -2.86603,-3.57283c-0.30033,-0.5178 -0.03366,-0.80259 0.22524,-1.06149c0.23301,-0.23301 0.5178,-0.59547 0.7767,-0.90616c0.25372,-0.31068 0.33657,-0.5178 0.51262,-0.85437c0.17088,-0.36246 0.08544,-0.64725 -0.04402,-0.90615c-0.12945,-0.2589 -1.15987,-2.79613 -1.58964,-3.80584c-0.41424,-1.00971 -0.84142,-0.88027 -1.15987,-0.88027c-0.29773,-0.02588 -0.64208,-0.02588 -0.98382,-0.02588c-0.34693,0 -0.90616,0.12945 -1.37736,0.62136c-0.4712,0.5178 -1.80194,1.76053 -1.80194,4.27186c0,2.51134 1.84596,4.945 2.10227,5.30747c0.2589,0.33657 3.63497,5.51458 8.80262,7.74113c1.23237,0.5178 2.1903,0.82848 2.94111,1.08738c1.23237,0.38836 2.35599,0.33657 3.24402,0.20712c0.99159,-0.15534 3.04985,-1.24272 3.47963,-2.45956c0.44013,-1.21683 0.44013,-2.22654 0.31068,-2.45955c-0.12945,-0.23301 -0.46601,-0.36247 -0.98382,-0.59548m-9.40068,12.84407l-0.02589,0c-3.05503,0 -6.08417,-0.82849 -8.72495,-2.38189l-0.62136,-0.37023l-6.47252,1.68286l1.73463,-6.29129l-0.41424,-0.64725c-1.70875,-2.71846 -2.6149,-5.85116 -2.6149,-9.07706c0,-9.39809 7.68934,-17.06155 17.15993,-17.06155c4.58253,0 8.88029,1.78642 12.11655,5.02268c3.23625,3.21036 5.02267,7.50812 5.02267,12.06476c-0.0078,9.3981 -7.69712,17.06155 -17.14699,17.06155m14.58906,-31.58846c-3.93529,-3.80584 -9.1133,-5.95471 -14.62789,-5.95471c-11.36055,0 -20.60848,9.2065 -20.61625,20.52564c0,3.61684 0.94757,7.14565 2.75211,10.26282l-2.92557,10.63564l10.93337,-2.85309c3.0136,1.63108 6.4052,2.4958 9.85634,2.49839l0.01037,0c11.36574,0 20.61884,-9.2091 20.62403,-20.53082c0,-5.48093 -2.14111,-10.64081 -6.03239,-14.51915',
    },
}

const ICON_PARAMS_DEFAULTS = {
    size: 32,
    borderRadius: 8,
}

/**
 * Social media share buttons
 */
const Share: React.FC<SharePropType> = ({
    classes,
    link,
    text,
    platforms = [
        PlatformName.facebook,
        PlatformName.vk,
        PlatformName.twitter,
        PlatformName.telegram,
        PlatformName.whatsapp,
    ],
    iconParams = {},
}) => {
    const icon = { ...ICON_PARAMS_DEFAULTS, ...iconParams }

    const { t } = useTranslation()

    const user = useAppSelector((state) => state.user)

    const excludePlatforms = useMemo<(keyof typeof PlatformName)[]>(() => {
        switch (user.country.isoCode) {
            case 'RU':
                return [PlatformName.facebook, PlatformName.twitter]
            case 'BY':
            case 'KZ':
                return []
            default:
                return [PlatformName.vk]
        }
    }, [user])

    const shareItems = useMemo(() => {
        return platforms.reduce<TShareItem[]>((acc, platformKey) => {
            if (platformKey in PLATFORMS && !excludePlatforms.includes(platformKey)) {
                const platform = PLATFORMS[platformKey]
                const { paramText = '', paramUrl = '', url } = platform
                const params = !paramUrl && paramText ? { [paramText]: `${text}%20${link}` } : { [paramText]: text, [paramUrl]: link }
                const urlSearchParams = setUrlSearchParams(params, true)
                const shareItem = { ...platform, key: platformKey, shareLink: `${url}?${urlSearchParams}` }

                return [...acc, shareItem]
            }

            return acc
        }, [])
    }, [excludePlatforms])

    const handlerClickCopy = () => {
        copyTextClipboard(`${link}\n${text}`)
            .then(() => {
                showAlertNotify({ type: 'success', message: t('Copied to clipboard') })
            })
            .catch(() => {
                showAlertNotify({ type: 'error', message: t('update_error') })
            })
    }

    return (
        <div className={cn(style.share, classes)}>
            {shareItems.map((item) => (
                <a
                    className={style.item}
                    style={{ backgroundColor: item.iconColor }}
                    href={item.shareLink}
                    target="_blank"
                    rel="noreferrer noopener"
                    aria-label="link"
                    key={item.key}
                >
                    <svg
                        viewBox={`0 0 ${item.iconViewBox} ${item.iconViewBox}`}
                        width={icon.size}
                        height={icon.size}
                    >
                        <path
                            d={item.iconPath}
                            fill={item.iconFillColor}
                        />
                    </svg>
                </a>
            ))}

            <Button
                classes={cn(style.item, style.item_default)}
                styleType="transparent"
                onClick={handlerClickCopy}
            >
                <svg viewBox="0 0 24 24" width={icon.size} height={icon.size}>
                    <path d="M12.344 4.446A5.092 5.092 0 0115.94 3a5.093 5.093 0 013.57 1.509 5.157 5.157 0 011.494 3.587 5.159 5.159 0 01-1.43 3.614l-.013.012-2.465 2.485a5.08 5.08 0 01-6.068.878 5.12 5.12 0 01-1.641-1.434 1 1 0 011.606-1.192c.267.361.608.659 1 .874a3.086 3.086 0 003.683-.535l2.459-2.478a3.159 3.159 0 00.87-2.206 3.158 3.158 0 00-.914-2.197A3.093 3.093 0 0015.923 5a3.092 3.092 0 00-2.18.874l-1.408 1.411a1 1 0 11-1.415-1.413l1.413-1.416.01-.01z" />
                    <path d="M8.733 8.605a5.08 5.08 0 014.24.306 5.118 5.118 0 011.641 1.434 1 1 0 01-1.606 1.192 3.119 3.119 0 00-1-.874 3.084 3.084 0 00-3.683.535l-2.46 2.478a3.159 3.159 0 00-.869 2.207c.007.826.336 1.614.913 2.196.577.582 1.356.91 2.169.917a3.092 3.092 0 002.178-.873l1.399-1.41a1 1 0 111.42 1.409l-1.406 1.416-.012.012a5.092 5.092 0 01-3.596 1.446 5.093 5.093 0 01-3.571-1.508A5.158 5.158 0 012.996 15.9a5.158 5.158 0 011.432-3.614l.012-.012 2.465-2.485a5.106 5.106 0 011.828-1.184z" />
                </svg>
            </Button>
        </div>
    )
}

export default Share
